import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../lib/permissions';
import { supabase } from '../lib/supabase';

const spinStyle = { width: '40px', height: '40px', border: '3px solid #F0EDE8', borderTopColor: '#FF6B35', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' };

function Loading() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFFDF7', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={spinStyle} />
        <p style={{ color: '#6A6A6A', fontSize: '0.9rem' }}>Loading…</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AccessDenied() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFFDF7', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ textAlign: 'center', maxWidth: '380px', padding: '2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1A1A1A', margin: '0 0 0.5rem' }}>Access Denied</h2>
        <p style={{ color: '#6A6A6A', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>You don't have permission to view this page. Contact a super admin if you think this is a mistake.</p>
        <a href="/admin" style={{ background: '#FF6B35', color: 'white', textDecoration: 'none', borderRadius: '10px', padding: '0.65rem 1.5rem', fontWeight: '700', fontSize: '0.9rem' }}>Go to Dashboard</a>
      </div>
    </div>
  );
}

// requiredPermission: { resource, action } — optional; omit to allow any authenticated+assigned user
export default function ProtectedRoute({ children, requiredPermission }) {
  const { session, role } = useAuth();
  const [mfaState, setMfaState] = useState(undefined); // undefined=checking, 'ok', 'challenge', 'setup'

  useEffect(() => {
    if (!session) { setMfaState('ok'); return; } // no session → login redirect handles it
    Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
    ]).then(([{ data: aalData }, { data: factorsData }]) => {
      const hasFactors = factorsData?.totp?.length > 0;
      if (!hasFactors) {
        setMfaState('setup');
      } else if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
        setMfaState('challenge');
      } else {
        setMfaState('ok');
      }
    });
  }, [session]);

  // Still loading session, role, or MFA state
  if (session === undefined || (session && role === undefined) || mfaState === undefined) return <Loading />;

  // Not logged in
  if (!session) return <Navigate to="/admin/login" replace />;

  // MFA not yet satisfied — redirect to the right step
  if (mfaState === 'setup')     return <Navigate to="/admin/mfa-setup"     replace />;
  if (mfaState === 'challenge') return <Navigate to="/admin/mfa-challenge" replace />;

  // Logged in but no role assigned yet
  if (!role) return <AccessDenied />;

  // Permission check
  if (requiredPermission && !can(role, requiredPermission.resource, requiredPermission.action)) {
    return <AccessDenied />;
  }

  return children;
}
